import React, { useState } from "react";
import {
  Drawer,
  Toolbar,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  Box,
  styled,
  Tooltip
} from "@mui/material";
import {
  ExpandLess,
  ExpandMore
} from "@mui/icons-material";

// Import semua icon yang dibutuhkan
import {
  Home as HomeIcon,
  People as PeopleIcon,
  Code as CodeIcon,
  ScienceOutlined as ScienceOutlinedIcon,
  PieChartOutlineOutlined as PieChartOutlineOutlinedIcon,
  ScaleOutlined as ScaleOutlinedIcon,
  LocalFloristOutlined as LocalFloristOutlinedIcon,
  AccountTreeOutlined as AccountTreeOutlinedIcon,
  MapOutlined as MapOutlinedIcon,
  StoreOutlined as StoreOutlinedIcon,
  LocalShippingOutlined as LocalShippingOutlinedIcon,
  PushPin as PushPinIcon,
  HelpOutline as HelpOutlineIcon,
  AssignmentTurnedInOutlined as AssignmentTurnedInOutlinedIcon,
  ShoppingCartOutlined as ShoppingCartOutlinedIcon,
  DashboardOutlined as DashboardOutlinedIcon,
  ReceiptOutlined as ReceiptOutlinedIcon,
  CategoryOutlined as CategoryOutlinedIcon,
  AttachMoneyOutlined as AttachMoneyOutlinedIcon,
  StraightenOutlined as StraightenOutlinedIcon,
  DescriptionOutlined as DescriptionOutlinedIcon,
  Agriculture as AgricultureIcon,
  Grass as GrassIcon,
  Shower as ShowerIcon,
  LocalShipping as LocalShippingIcon,
  FactoryOutlined as FactoryOutlinedIcon,
  LegendToggleOutlined as LegendToggleOutlinedIcon,
  HistoryOutlined as HistoryOutlinedIcon,
  YardOutlined as YardOutlinedIcon,
  SpaOutlined as SpaOutlinedIcon,
  BugReportOutlined as BugReportOutlinedIcon,
  ScatterPlotOutlined as ScatterPlotOutlinedIcon,
  Label as LabelIcon,
  Category as CategoryIcon,
  Straighten as StraightenIcon,
  Inventory2Outlined as Inventory2OutlinedIcon,
  FactCheckOutlined as FactCheckOutlinedIcon,
  BatchPredictionOutlined as BatchPredictionOutlinedIcon,
  History as HistoryIcon
} from "@mui/icons-material";
import PlaylistAddCheckCircleOutlinedIcon from '@mui/icons-material/PlaylistAddCheckCircleOutlined';
import InventoryOutlinedIcon from '@mui/icons-material/InventoryOutlined';
import AnalyticsOutlinedIcon from '@mui/icons-material/AnalyticsOutlined';

// Function untuk mendapatkan icon berdasarkan nama
const getIcon = (iconName) => {
  switch (iconName) {
    case "Home": return <HomeIcon />;
    case "People": return <PeopleIcon />;
    case "Code": return <CodeIcon />;
    case "ScienceOutlined": return <ScienceOutlinedIcon />;
    case "PieChartOutlineOutlined": return <PieChartOutlineOutlinedIcon />;
    case "ScaleOutlined": return <ScaleOutlinedIcon />;
    case "LocalFloristOutlined": return <LocalFloristOutlinedIcon />;
    case "AccountTreeOutlined": return <AccountTreeOutlinedIcon />;
    case "MapOutlined": return <MapOutlinedIcon />;
    case "StoreOutlined": return <StoreOutlinedIcon />;
    case "LocalShippingOutlined": return <LocalShippingOutlinedIcon />;
    case "PushPin": return <PushPinIcon />;
    case "HelpOutline": return <HelpOutlineIcon />;
    case "AssignmentTurnedInOutlined": return <AssignmentTurnedInOutlinedIcon />;
    case "DashboardOutlined": return <DashboardOutlinedIcon />;
    case "ReceiptOutlined": return <ReceiptOutlinedIcon />;
    case "CategoryOutlined": return <CategoryOutlinedIcon />;
    case "AttachMoneyOutlined": return <AttachMoneyOutlinedIcon />;
    case "StraightenOutlined": return <StraightenOutlinedIcon />;
    case "DescriptionOutlined": return <DescriptionOutlinedIcon />;
    case "PlaylistAddCheckCircleOutlined": return <PlaylistAddCheckCircleOutlinedIcon />;
    case "InventoryOutlined": return <InventoryOutlinedIcon />;
    case "AnalyticsOutlined": return <AnalyticsOutlinedIcon />;
    case "Agriculture": return <AgricultureIcon />;
    case "Grass": return <GrassIcon />;
    case "Shower": return <ShowerIcon />;
    case "LocalShipping": return <LocalShippingIcon />;
    case "FactoryOutlined": return <FactoryOutlinedIcon />;
    case "LegendToggleOutlined": return <LegendToggleOutlinedIcon />;
    case "HistoryOutlined": return <HistoryOutlinedIcon />;
    case "YardOutlined": return <YardOutlinedIcon />;
    case "SpaOutlined": return <SpaOutlinedIcon />;
    case "BugReportOutlined": return <BugReportOutlinedIcon />;
    case "ScatterPlotOutlined": return <ScatterPlotOutlinedIcon />;
    case "Labels": return <LabelIcon />;
    case "Category": return <CategoryIcon/>;
    case "Straighten": return <StraightenIcon/>;
    case "Inventory2Outlined": return <Inventory2OutlinedIcon/>;
    case "FactCheckOutlined": return <FactCheckOutlinedIcon/>;
    case "BatchPredictionOutlined": return <BatchPredictionOutlinedIcon/>;
    case "History": return <HistoryIcon/>;
    default: return <HomeIcon />;
  }
};

// Styled component untuk scrollbar yang custom
const ScrollableList = styled(List)(({ theme, isHovered }) => ({
  overflowY: 'auto',
  overflowX: 'hidden',
  maxHeight: 'calc(100vh - 120px)', // Tinggi maksimal dikurangi toolbar dan margin
  '&::-webkit-scrollbar': {
    width: isHovered ? '6px' : '0px', // Lebar scrollbar muncul saat hover
    transition: 'width 0.3s ease',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
    borderRadius: '10px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: isHovered ? theme.palette.grey[400] : 'transparent',
    borderRadius: '10px',
    border: '2px solid transparent',
    backgroundClip: 'padding-box',
    '&:hover': {
      backgroundColor: theme.palette.grey[500],
    }
  },
  '&::-webkit-scrollbar-thumb:active': {
    backgroundColor: theme.palette.grey[600],
  },
  // Untuk Firefox
  scrollbarWidth: 'thin',
  scrollbarColor: isHovered ? `${theme.palette.grey[400]} transparent` : 'transparent transparent',
}));

// Styled component untuk drawer container
const DrawerContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
  overflow: 'hidden', // Mencegah scroll di container utama
}));

// Styled component untuk ListItem aktif
const ActiveListItem = styled(ListItem)(({ theme, isactive }) => ({
  backgroundColor: isactive === 'true' ? '#f2f9ff' : 'inherit',
  borderRadius: isactive === 'true' ? '0px 50px 50px 0px' : '0px',
  marginRight: isactive === 'true' ? '8px' : '0px',
  '& .MuiListItemIcon-root': {
    color: isactive === 'true' ? theme.palette.primary.main : 'inherit',
  },
  '& .MuiListItemText-primary': {
    color: isactive === 'true' ? theme.palette.primary.main : 'inherit',
    fontWeight: isactive === 'true' ? '600' : '400',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  '& .MuiSvgIcon-root': {
    color: isactive === 'true' ? theme.palette.primary.main : 'inherit',
  },
  '&:hover': {
    backgroundColor: isactive === 'true' ? '#e6f2ff' : 'rgba(0, 0, 0, 0.04)',
    borderRadius: isactive === 'true' ? '0px 50px 50px 0px' : '0px',
  },
}));

// Styled component untuk submenu item aktif
const ActiveSubListItem = styled(ListItem)(({ theme, isactive }) => ({
  backgroundColor: isactive === 'true' ? '#f2f9ff' : 'inherit',
  borderRadius: isactive === 'true' ? '0px 50px 50px 0px' : '0px',
  marginRight: isactive === 'true' ? '8px' : '0px',
  '& .MuiListItemIcon-root': {
    color: isactive === 'true' ? theme.palette.primary.main : 'inherit',
  },
  '& .MuiListItemText-primary': {
    color: isactive === 'true' ? theme.palette.primary.main : 'inherit',
    fontWeight: isactive === 'true' ? '600' : '400',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  '&:hover': {
    backgroundColor: isactive === 'true' ? '#e6f2ff' : 'rgba(0, 0, 0, 0.04)',
    borderRadius: isactive === 'true' ? '0px 50px 50px 0px' : '0px',
  },
}));

// Styled component untuk label dengan ellipsis
const LabelTypography = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: 'block',
  width: '100%',
}));

function MainDrawer({
  drawerWidth,
  mobileOpen,
  handleDrawerToggle,
  container,
  menuItems,
  navigate,
  activePath
}) {
  const [isDrawerHovered, setIsDrawerHovered] = useState(false);
  const [isListHovered, setIsListHovered] = useState(false);

  const handleDrawerMouseEnter = () => {
    setIsDrawerHovered(true);
  };

  const handleDrawerMouseLeave = () => {
    setIsDrawerHovered(false);
  };

  const handleListMouseEnter = () => {
    setIsListHovered(true);
  };

  const handleListMouseLeave = () => {
    setIsListHovered(false);
  };

  // Helper function untuk mengecek apakah item aktif
  const isItemActive = (item) => {
    if (!item.isCollapsible && item.path === activePath) {
      return true;
    }
    
    if (item.isCollapsible && item.subItems) {
      return item.subItems.some(subItem => subItem.path === activePath);
    }
    
    return false;
  };

  // Helper function untuk menentukan apakah perlu tooltip
  const needsTooltip = (text) => {
    // Tampilkan tooltip jika teks lebih dari 25 karakter
    return text && text.length > 25;
  };

  const drawer = (
    <DrawerContainer
      onMouseEnter={handleDrawerMouseEnter}
      onMouseLeave={handleDrawerMouseLeave}
    >
      <Toolbar>
        <Box>
          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
            FPMS
          </div>
          <div style={{ fontSize: '0.8em', color: '#666' }}>
            Fresh Pine Monitoring System
          </div>
        </Box>
      </Toolbar>
      <Divider />
      <ScrollableList 
        sx={{ cursor: "pointer" }}
        isHovered={isDrawerHovered || isListHovered}
        onMouseEnter={handleListMouseEnter}
        onMouseLeave={handleListMouseLeave}
      >
        {menuItems.map((item, index) => (
          <React.Fragment key={index}>
            {item.isLabel ? (
              <>
                <Divider sx={{ my: 1 }} />
                <Tooltip 
                  title={needsTooltip(item.text) ? item.text : ''} 
                  placement="right"
                  arrow
                  disableHoverListener={!needsTooltip(item.text)}
                >
                  <ListItem>
                    <ListItemText primary={
                      <LabelTypography variant="overline">
                        {item.text}
                      </LabelTypography>
                    } />
                  </ListItem>
                </Tooltip>
              </>
            ) : (
              <Tooltip 
                title={needsTooltip(item.text) ? item.text : ''} 
                placement="right"
                arrow
                disableHoverListener={!needsTooltip(item.text)}
              >
                <ActiveListItem
                  button
                  onClick={
                    item.isCollapsible
                      ? item.toggleHandler
                      : () => navigate(item.path)
                  }
                  isactive={isItemActive(item).toString()}
                >
                  <ListItemIcon>{getIcon(item.icon)}</ListItemIcon>
                  <ListItemText 
                    primary={item.text}
                    sx={{
                      '& .MuiListItemText-primary': {
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }
                    }}
                  />
                  {item.isCollapsible && (item.openState ? <ExpandLess /> : <ExpandMore />)}
                </ActiveListItem>
              </Tooltip>
            )}

            {item.isCollapsible && (
              <Collapse
                in={item.openState}
                timeout="auto"
                unmountOnExit
                sx={{
                  '& .MuiCollapse-wrapper': {
                    marginTop: '8px',
                  }
                }}
              >
                <List component="div" disablePadding>
                  {item.subItems.map((subItem, subIndex) => (
                    <Tooltip 
                      key={`tooltip-${subIndex}`}
                      title={needsTooltip(subItem.text) ? subItem.text : ''} 
                      placement="right"
                      arrow
                      disableHoverListener={!needsTooltip(subItem.text)}
                    >
                      <ActiveSubListItem
                        button
                        sx={{ pl: 4 }}
                        isactive={(activePath === subItem.path).toString()}
                        onClick={() => navigate(subItem.path)}
                      >
                        <ListItemIcon>{getIcon(subItem.icon)}</ListItemIcon>
                        <ListItemText 
                          primary={subItem.text}
                          sx={{
                            '& .MuiListItemText-primary': {
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }
                          }}
                        />
                      </ActiveSubListItem>
                    </Tooltip>
                  ))}
                </List>
              </Collapse>
            )}
          </React.Fragment>
        ))}
      </ScrollableList>
    </DrawerContainer>
  );

  return (
    <Box 
      component="nav" 
      sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }} 
      aria-label="menu drawer"
    >
      <Drawer
        container={container}
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{ 
          display: { xs: "block", sm: "none" }, 
          "& .MuiDrawer-paper": { 
            width: drawerWidth,
            overflow: 'hidden', // Pastikan tidak ada scroll default
          } 
        }}
      >
        {drawer}
      </Drawer>
      <Drawer
        variant="permanent"
        sx={{ 
          display: { xs: "none", sm: "block" }, 
          "& .MuiDrawer-paper": { 
            width: drawerWidth, 
            boxSizing: "border-box",
            overflow: 'hidden', // Pastikan tidak ada scroll default
          } 
        }}
        open
      >
        {drawer}
      </Drawer>
    </Box>
  );
}

export default MainDrawer;